/**
 * Contracts for module "billing". Owner: billing.
 * Put zod request/response/event schemas for this module here; other modules and the portal
 * import them as `import { ... } from '@qm/contracts/billing'`. Never import another module's internals.
 *
 * Money rule (QM-C06-007): every amount is an INTEGER in minor units plus an ISO currency.
 * Prices are NEVER accepted from a client (NFR-C03-01): request schemas below have no amount fields,
 * and zod strips unknown keys, so a tampered `amount`/`price`/`unit_amount` is ignored.
 */
import { z } from 'zod';

export const CurrencySchema = z.enum(['USD', 'AED']);
export type Currency = z.infer<typeof CurrencySchema>;

export const MoneySchema = z.object({
  amountMinor: z.number().int(),
  currency: CurrencySchema,
});
export type Money = z.infer<typeof MoneySchema>;

export const SkuSchema = z.string().min(1).max(64).regex(/^[a-z0-9_]+$/);

export const CatalogueKindSchema = z.enum(['one_time', 'subscription', 'add_on']);
export const CatalogueItemSchema = z.object({
  sku: SkuSchema,
  kind: CatalogueKindSchema,
  name: z.string(),
  price: MoneySchema,
  interval: z.enum(['month', 'year']).nullable(),
  /** false until Quixo Finance approves the price (DEC-07). Unsellable items cannot be checked out. */
  sellable: z.boolean(),
  /** wholesale price for partner resale (QM-D06-003); null = not resellable */
  wholesale: MoneySchema.nullable(),
  /**
   * Wholesale in the SKU's other charge currencies (DEC-07d, owner decision 30 Oct 2026: wholesale = 70% of list for
   * every plan, in AED and USD). Same currencies as altPrices; `wholesale` stays the base-currency wholesale.
   */
  altWholesale: z.array(MoneySchema).optional(),
  complimentaryEligible: z.boolean(),
  /**
   * Extra charge currencies for the same SKU (owner decision 25 Sep 2026: CRM plans sell in AED and USD; USD = AED at
   * the 3.6725 peg, rounded to whole dollars). Each has its own Stripe price, mapped as `<sku>@<CURRENCY>`.
   */
  altPrices: z.array(MoneySchema).optional(),
  /** Contact-sales tier: listed (name only, no price) and never self-serve (always sellable: false). */
  contactSales: z.boolean().optional(),
  /**
   * Analyst-reviewed Quixo Audit (DEC-18). List prices exclude 5% VAT. The fee is credited against the first retainer
   * month when the client signs a retainer within `retainerCredit.windowDays` of paying. `paidMedia` tiers need live
   * campaigns; that is checked at analyst intake, never at checkout.
   */
  analyst: z.object({
    paidMedia: z.boolean(),
    retainerCredit: z.object({ windowDays: z.number().int().positive(), appliesTo: z.literal('first_retainer_month') }),
    intakeRequirement: z.literal('live_campaigns').nullable(),
  }).optional(),
});
export type CatalogueItem = z.infer<typeof CatalogueItemSchema>;

export const ReferralInputSchema = z.object({
  code: z.string().min(4).max(40).regex(/^[A-Za-z0-9_-]+$/),
  /** where the browser found the code: URL parameter or first-party cookie */
  source: z.enum(['url', 'cookie']),
});

/**
 * Quixo Audit tiers sold on the website (owner decision 25 Sep 2026: all three tiers are on sale).
 * The tier picks the catalogue SKU; the price always comes from the server catalogue, never from the client.
 */
export const AUDIT_TIERS = ['essential', 'pro', 'enterprise'] as const;
export const AuditTierSchema = z.enum(AUDIT_TIERS);
export type AuditTier = z.infer<typeof AuditTierSchema>;
export const DEFAULT_AUDIT_TIER: AuditTier = 'pro';
/** tier -> one-time catalogue SKU (apps/api/src/modules/billing/catalogue.ts). */
export const AUDIT_TIER_SKUS: Readonly<Record<AuditTier, string>> = {
  essential: 'audit_essential',
  pro: 'audit_pro',
  enterprise: 'audit_enterprise',
};
/** catalogue SKU -> tier; null for anything that is not a Quixo Audit tier SKU. */
export function auditTierForSku(sku: string | null | undefined): AuditTier | null {
  for (const t of AUDIT_TIERS) if (AUDIT_TIER_SKUS[t] === sku) return t;
  return null;
}

/** Analyst-reviewed Quixo Audit SKUs (DEC-18). AED is the base currency, USD the alternative (AED / 3.6725, rounded). */
export const ANALYST_SKUS = { base: 'audit_analyst', paidMedia: 'audit_analyst_paid_media' } as const;
export const ANALYST_RETAINER_CREDIT_DAYS = 30;
export const isAnalystSku = (sku: string | null | undefined): boolean => sku === ANALYST_SKUS.base || sku === ANALYST_SKUS.paidMedia;

/** POST /v1/billing/checkout (anonymous one-step Quixo Audit purchase, QM-A01-008). */
export const CheckoutRequestSchema = z.object({
  /**
   * Legacy field: older website builds send sku 'audit_pro'. Must be a Quixo Audit tier SKU when present and agree
   * with `tier` when both are sent.
   */
  sku: SkuSchema.optional(),
  /**
   * Quixo Audit tier. Optional: the tier stored on the verified intake request wins, then this, then 'pro'.
   * A tier that differs from the one stored on the intake request is refused (400 tier_mismatch). Anything other than
   * 'essential' | 'pro' | 'enterprise' -> 400.
   */
  tier: AuditTierSchema.optional(),
  email: z.string().email().max(254),
  website: z.string().url().max(2048),
  /** token issued by identity after the email was verified */
  emailVerificationToken: z.string().min(16).max(256),
  /**
   * The verified website intake request (qm-audit aq_intake_requests, QM-A01-009). Required: it is persisted on the
   * order and the paid entitlement so qm-audit can create the job (QM-A01-004) and eligibility answers 200 after payment.
   */
  requestId: z.string().regex(/^aqr_[A-Za-z0-9]{8,60}$/),
  /**
   * Charge currency the buyer picked (owner decision 26 Sep 2026: audits are sold in USD or AED). Must be the SKU's
   * base currency (USD) or one of its catalogue altPrices (AED); anything else -> 400 currency_not_offered. Omitted =
   * USD. The amount always comes from the catalogue for that currency, never from the client.
   */
  currency: CurrencySchema.optional(),
  /** display currency only; defaults to the charge currency */
  displayCurrency: CurrencySchema.optional(),
  referral: ReferralInputSchema.optional(),
  /** marketing/analytics cookie consent; without it cookie-sourced referrals are dropped */
  cookieConsent: z.boolean().default(false),
  /**
   * Website language for the Stripe return page (/ar/audit/status for 'ar'). Optional fallback only: the API prefers the
   * locale stored on the verified intake request. Anything other than 'en' | 'ar' -> 400.
   */
  locale: z.enum(['en', 'ar']).optional(),
});
export type CheckoutRequest = z.infer<typeof CheckoutRequestSchema>;

export const CheckoutResponseSchema = z.object({
  orderId: z.string(),
  tier: AuditTierSchema,
  statusToken: z.string(),
  checkoutUrl: z.string().url(),
  /** true for a free launch audit (no Stripe; checkoutUrl is the status page) */
  free: z.boolean().optional(),
  charge: MoneySchema,
  display: z.object({ amountMinor: z.number().int(), currency: CurrencySchema, chargeCurrencyNote: z.string() }),
});

export const OrderStatusSchema = z.enum(['awaiting_payment', 'queued', 'payment_failed', 'refunded', 'expired']);

/**
 * Super Admin complimentary grants (DEC-09, owner decision 30 Oct 2026): at most `perMonth` grants per UTC calendar
 * month across the platform, `defaultExpiryDays` when no expiry is given (1 to `maxExpiryDays` allowed), and never a
 * tier above `maxTier`. A granted audit runs on the Pro AI budget (AI_SPEND_CAPS.complimentaryAuditTier in @qm/ai).
 */
export const COMPLIMENTARY_GRANT_RULES = { perMonth: 10, defaultExpiryDays: 14, maxExpiryDays: 90, maxTier: 'pro' } as const satisfies {
  perMonth: number; defaultExpiryDays: number; maxExpiryDays: number; maxTier: AuditTier;
};

/** True when a SKU's audit tier is above COMPLIMENTARY_GRANT_RULES.maxTier (non-audit SKUs: false). */
export function complimentaryTierTooHigh(sku: string): boolean {
  const tier = auditTierForSku(sku);
  return tier !== null && AUDIT_TIERS.indexOf(tier) > AUDIT_TIERS.indexOf(COMPLIMENTARY_GRANT_RULES.maxTier);
}

export const GrantComplimentarySchema = z.object({
  tenantId: z.string().min(1).max(64),
  requestId: z.string().min(1).max(64),
  sku: SkuSchema,
  reason: z.string().trim().min(3).max(500),
  expiresInDays: z.number().int().min(1).max(COMPLIMENTARY_GRANT_RULES.maxExpiryDays).default(COMPLIMENTARY_GRANT_RULES.defaultExpiryDays),
  reportScope: z.string().min(1).max(120).default('growth_audit'),
});
export type GrantComplimentary = z.infer<typeof GrantComplimentarySchema>;

export const RevokeEntitlementSchema = z.object({ reason: z.string().trim().min(3).max(500) });

/**
 * Enterprise CRM plan (DEC-07e, owner decision 30 Oct 2026): priced per deal, invoiced by hand in Stripe by staff; a
 * Super Admin grant turns on the `plan_enterprise` entitlement. No amount is accepted: the deal price lives only on the
 * Stripe invoice. `dealReference` is the Stripe invoice / subscription id or the signed deal reference, for the audit
 * trail. `expiresInDays` omitted = until revoked.
 */
export const ENTERPRISE_PLAN_SKU = 'plan_enterprise';
export const GrantEnterpriseSchema = z.object({
  tenantId: z.string().min(1).max(64),
  reason: z.string().trim().min(3).max(500),
  dealReference: z.string().trim().min(3).max(120),
  expiresInDays: z.number().int().min(1).max(1_095).optional(),
});
export type GrantEnterprise = z.infer<typeof GrantEnterpriseSchema>;

export const EntitlementSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  entitlementType: z.enum(['paid', 'complimentary']),
  sku: z.string(),
  requestId: z.string().nullable(),
  reportScope: z.string().nullable(),
  reason: z.string().nullable(),
  actorId: z.string().nullable(),
  approvedAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  revokedAt: z.string().nullable(),
  status: z.enum(['active', 'expired', 'revoked']),
});
export type Entitlement = z.infer<typeof EntitlementSchema>;

/** currency: charge currency for the plan (AED base or a catalogue altPrice such as USD); omitted = the SKU's base currency. */
export const SubscriptionChangeSchema = z.object({ planSku: SkuSchema, currency: CurrencySchema.optional() });
export const SubscriptionCancelSchema = z.object({ atPeriodEnd: z.boolean().default(true) });

export const RefundRequestSchema = z.object({
  paymentId: z.string().min(1).max(64),
  /** partial refund in minor units; omitted = full remaining amount */
  amountMinor: z.number().int().positive().optional(),
  reason: z.string().trim().min(3).max(500),
});

export const UsageConsumeSchema = z.object({
  meter: z.string().min(1).max(64).regex(/^[a-z0-9_.]+$/),
  quantity: z.number().int().positive().max(1_000_000),
});
export const UsageLimitSchema = z.object({
  tenantId: z.string().min(1).max(64),
  meter: z.string().min(1).max(64).regex(/^[a-z0-9_.]+$/),
  limit: z.number().int().min(0),
  overageAllowed: z.boolean().default(false),
  overageUnitMinor: z.number().int().min(0).default(0),
  currency: CurrencySchema.default('USD'),
});

export const MarkupSchema = z.object({
  /** basis points: 3000 = 30 %. Negative (below the wholesale floor) is rejected. */
  markupBps: z.number().int().min(0).max(50_000),
  descriptorSuffix: z.string().trim().min(2).max(12).regex(/^[A-Za-z0-9 ]+$/).optional(),
});

export const ReconcileRequestSchema = z.object({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) });

/** Queue events emitted by billing. Body shape follows { type, tenantId, idempotencyKey, payload }. */
export const BILLING_EVENTS = {
  entitlementGranted: 'billing.entitlement.granted',
  entitlementRevoked: 'billing.entitlement.revoked',
  paymentFailed: 'billing.funnel.payment_failed',
  reconciliationAlert: 'billing.reconciliation.alert',
  dunningNotice: 'billing.dunning.notice',
} as const;

export const EntitlementGrantedEventSchema = z.object({
  type: z.literal(BILLING_EVENTS.entitlementGranted),
  tenantId: z.string(),
  idempotencyKey: z.string(),
  payload: z.object({
    entitlementId: z.string(),
    entitlementType: z.enum(['paid', 'complimentary']),
    sku: z.string(),
    /** Quixo Audit tier derived from the SKU on the server (absent for non-audit SKUs) */
    tier: AuditTierSchema.optional(),
    requestId: z.string().nullable(),
    orderId: z.string().nullable(),
  }),
});

/**
 * Price changes (website CMS / staff console, owner decision 25 Sep 2026). Proposed by staff_admin, staff_content or
 * staff_finance; approved by a different staff_finance user (four-eyes). A charge price needs its Stripe price id,
 * checked against Stripe on approval. Wholesale changes are partner-only and never public.
 */
export const PriceChangeKindSchema = z.enum(['price', 'wholesale']);
export const PriceChangeProposeSchema = z.object({
  sku: SkuSchema,
  kind: PriceChangeKindSchema,
  currency: CurrencySchema,
  amountMinor: z.number().int().positive().max(100_000_000),
  reason: z.string().trim().min(5).max(500),
}).strict();
export type PriceChangePropose = z.infer<typeof PriceChangeProposeSchema>;
export const PriceChangeApproveSchema = z.object({
  /** required for kind 'price': the Stripe price for exactly this amount, currency and interval */
  stripePriceId: z.string().max(200).regex(/^price_[A-Za-z0-9_]+$/).optional(),
  note: z.string().trim().max(500).optional(),
}).strict();
export const PriceChangeRejectSchema = z.object({ note: z.string().trim().min(3).max(500) }).strict();
export interface PriceChangeView {
  id: string; sku: string; kind: 'price' | 'wholesale'; currency: Currency; amountMinor: number; reason: string;
  status: 'proposed' | 'approved' | 'rejected' | 'withdrawn'; stripePriceId: string | null;
  proposedBy: string; proposedAt: string; decidedBy: string | null; decidedAt: string | null; decisionNote: string | null;
}

/**
 * WhatsApp add-on metered billing per client (Pending item 7; billing side of QM-B04-002). Usage comes from wa-core's
 * wa_usage_counters and is reported to Stripe Billing Meters. Off unless this flag is on for the tenant (no flag row =
 * off in every environment).
 */
export const WA_ADDON_FLAG = 'billing.wa_addon_metering';
export const WA_ADDON_SKUS = ['addon_wa_messages', 'addon_wa_ai_replies'] as const;
export type WaAddonSku = (typeof WA_ADDON_SKUS)[number];
export const WaAddonEnrolSchema = z.object({ tenantId: z.string().min(1).max(64) }).strict();
export const WaAddonRemoveSchema = z.object({ reason: z.string().trim().min(3).max(500) }).strict();
